import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import DateTimeQuarterInput from "@/components/DateTimeQuarterInput";
import { Loader2 } from "lucide-react";

function daysInMonth(year, month) {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
}

function DateSelect({ value, onChange, minYear, maxYear, testId }) {
  const { t } = useTranslation();
  const meses = t("checkout.meses", { returnObjects: true });
  const [yv = "", mv = "", dv = ""] = (value || "").split("-");
  const [y, setY] = useState(yv);
  const [m, setM] = useState(mv);
  const [d, setD] = useState(dv);

  // si el padre resetea el valor externamente (p.ej. limpiar formulario), sincroniza
  useEffect(() => {
    const [yy = "", mm = "", dd = ""] = (value || "").split("-");
    setY(yy); setM(mm); setD(dd);
  }, [value]);

  const years = [];
  for (let yr = maxYear; yr >= minYear; yr--) years.push(String(yr));
  const days = Array.from({ length: daysInMonth(y, m) }, (_, i) => String(i + 1).padStart(2, "0"));

  const emit = (ny, nm, nd) => {
    setY(ny); setM(nm); setD(nd);
    if (!ny || !nm || !nd) { onChange(""); return; }
    const safeDay = String(Math.min(Number(nd), daysInMonth(ny, nm))).padStart(2, "0");
    onChange(`${ny}-${nm}-${safeDay}`);
  };

  return (
    <div className="grid grid-cols-3 gap-2">
      <select className="pc-input" value={d} onChange={e => emit(y, m, e.target.value)} data-testid={`${testId}-day`}>
        <option value="">{t("checkout.dia")}</option>
        {days.map(dd => <option key={dd} value={dd}>{dd}</option>)}
      </select>
      <select className="pc-input" value={m} onChange={e => emit(y, e.target.value, d)} data-testid={`${testId}-month`}>
        <option value="">{t("checkout.mes")}</option>
        {meses.map((nombre, i) => <option key={nombre} value={String(i + 1).padStart(2, "0")}>{nombre}</option>)}
      </select>
      <select className="pc-input" value={y} onChange={e => emit(e.target.value, m, d)} data-testid={`${testId}-year`}>
        <option value="">{t("checkout.anio")}</option>
        {years.map(yy => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </div>
  );
}


function validarDniNie(valor) {
  const v = (valor || "").toUpperCase().replace(/[\s-]/g, "");
  const tabla = "TRWAGMYFPDXBNJZSQVHLCKE";
  if (/^[0-9]{8}[A-Z]$/.test(v)) {
    return tabla[parseInt(v.slice(0, 8), 10) % 23] === v[8];
  }
  if (/^[XYZ][0-9]{7}[A-Z]$/.test(v)) {
    const prefijo = { X: "0", Y: "1", Z: "2" }[v[0]];
    return tabla[parseInt(prefijo + v.slice(1, 8), 10) % 23] === v[8];
  }
  return true;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function uploadDoc(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`/api/upload?kind=${encodeURIComponent(kind)}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url; // e.g. /api/files/pillauncoche/uploads/dni/uuid.jpg
}

// Verificación de teléfono por SMS (Didit). Solo se renderiza si phone_verification_enabled === true.
function PhoneVerification({ phoneNumber, verified, onVerified, testIdPrefix = "phone-verify" }) {
  const { t } = useTranslation();
  const [step, setStep] = useState("idle"); // idle | sent | verified
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);

  const toE164 = (raw) => {
    const digits = (raw || "").replace(/[^\d+]/g, "");
    if (digits.startsWith("+")) return digits;
    return `+34${digits}`; // asume España si no se indica prefijo
  };

  const sendCode = async () => {
    if (!phoneNumber) { toast.error(t("checkout.phoneIntroduceTelefono")); return; }
    setSending(true);
    try {
      await axios.post("/api/verify-phone/send", { phone_number: toE164(phoneNumber) });
      setStep("sent");
      toast.success(t("checkout.phoneCodigoEnviado"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("checkout.phoneNoSePudoEnviar"));
    } finally { setSending(false); }
  };

  const checkCode = async () => {
    if (!code) return;
    setChecking(true);
    try {
      const { data } = await axios.post("/api/verify-phone/check", { phone_number: toE164(phoneNumber), code });
      if (data.verified) {
        setStep("verified");
        onVerified(true);
        toast.success(t("checkout.phoneVerificadoOk"));
      } else {
        toast.error(t("checkout.phoneCodigoIncorrecto"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("checkout.phoneNoSePudoVerificar"));
    } finally { setChecking(false); }
  };

  if (verified || step === "verified") {
    return <div className="text-xs text-green-600 mt-1" data-testid={`${testIdPrefix}-ok`}>{t("checkout.phoneVerificadoOk")}</div>;
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-widget`}>
      {step === "idle" && (
        <button type="button" onClick={sendCode} disabled={sending} className="text-xs px-2 py-1 bg-neutral-200 rounded hover:bg-neutral-300" data-testid={`${testIdPrefix}-send-btn`}>
          {sending ? t("checkout.phoneEnviando") : t("checkout.phoneVerificarSms")}
        </button>
      )}
      {step === "sent" && (
        <>
          <input className="pc-input text-xs w-24" placeholder={t("checkout.phoneCodigoPlaceholder")} value={code} onChange={e => setCode(e.target.value)} data-testid={`${testIdPrefix}-code-input`} />
          <button type="button" onClick={checkCode} disabled={checking || !code} className="text-xs px-2 py-1 bg-neutral-800 text-white rounded hover:bg-neutral-700" data-testid={`${testIdPrefix}-check-btn`}>
            {checking ? t("checkout.phoneComprobando") : t("checkout.phoneComprobar")}
          </button>
          <button type="button" onClick={sendCode} disabled={sending} className="text-xs text-neutral-500 underline">
            {t("checkout.phoneReenviar")}
          </button>
        </>
      )}
    </div>
  );
}

export default function Checkout() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { phone_verification_enabled, id_verification_enabled, rental_min_hours, precio_km_extra } = useConfig();
  const precioPack = (Number(precio_km_extra) * 100).toFixed(2);
  const vid = params.get("vid");
  const { user } = useAuth();
  const conductor1PreVerificado = id_verification_enabled && user?.documento_verificado_status === "Approved";
  const [vehicle, setVehicle] = useState(null);
  const [dates, setDates] = useState({
    fecha_recogida: params.get("pickup") || "",
    fecha_devolucion: params.get("dropoff") || "",
  });
  const [options, setOptions] = useState({
    reduccion_franquicia: false,
    conductor_extra: false,
    conduccion_extranjero: false,
    packs_km: 0,
  });
  const [pricing, setPricing] = useState(null);
  const [cuponInput, setCuponInput] = useState("");
  const [cuponAplicado, setCuponAplicado] = useState("");
  const [customer, setCustomer] = useState({
    nombre: "", apellidos: "", dni: "", direccion: "",
    telefono: "", email: "", fecha_nacimiento: "", fecha_expedicion_carnet: "", fecha_validez_carnet: "",
  });
  const [tieneConductor2, setTieneConductor2] = useState(false);
  const [conductor2, setConductor2] = useState({
    nombre: "", apellidos: "", dni: "", direccion: "",
    telefono: "", fecha_nacimiento: "", fecha_expedicion_carnet: "", fecha_validez_carnet: "", observaciones: "",
  });
  const [dniError2, setDniError2] = useState("");
  const [files, setFiles] = useState({ dni_frontal: null, dni_trasero: null, carnet_frontal: null, carnet_trasero: null });
  const [loading, setLoading] = useState(false);
  const [dniError, setDniError] = useState("");
  const [telefonoVerificado, setTelefonoVerificado] = useState(false);
  const [verifConductor1, setVerifConductor1] = useState(null); // { verification_id, status }
  const [verifConductor2, setVerifConductor2] = useState(null);
  const [verificando, setVerificando] = useState(""); // "conductor1" | "conductor2" | ""

  useEffect(() => {
    if (vid) axios.get(`/api/vehicles/${vid}`).then(r => setVehicle(r.data)).catch(() => { });
  }, [vid]);

  // Restaura el formulario tras volver de la verificación Didit (redirect de página completa)
  // y actualiza el estado de validación del conductor que acaba de completar el proceso.
  useEffect(() => {
    if (!vid) return;
    const saved = localStorage.getItem("pc_checkout_form");
    if (saved) {
      try {
        const f = JSON.parse(saved);
        if (f.vid === vid) {
          setDates(f.dates); setOptions(f.options); setCustomer(f.customer);
          setTieneConductor2(f.tieneConductor2); setConductor2(f.conductor2);
          setTelefonoVerificado(f.telefonoVerificado);
          if (f.verifConductor1) setVerifConductor1(f.verifConductor1);
          if (f.verifConductor2) setVerifConductor2(f.verifConductor2);
        }
      } catch { /* ignore */ }
    }
    const verificationId = params.get("verification_id");
    const driver = params.get("driver");
    if (verificationId && driver) {
      axios.get(`/api/verificacion/${verificationId}/estado`)
        .then(r => {
          const result = { verification_id: verificationId, status: r.data.status };
          if (driver === "conductor1") setVerifConductor1(result); else setVerifConductor2(result);
          if (r.data.status === "Approved") toast.success(t("checkout.verificacionOk"));
          else toast.error(t("checkout.verificacionNoAprobada"));
        })
        .catch(() => toast.error(t("checkout.verificacionError")))
        .finally(() => nav(`/checkout?vid=${vid}`, { replace: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid]);

  useEffect(() => {
    if (user) setCustomer(c => ({ ...c, email: c.email || user.email || "", nombre: c.nombre || (user.name || "").split(" ")[0] || "" }));
  }, [user]);

  const dateOrderError = Boolean(
    dates.fecha_recogida && dates.fecha_devolucion &&
    new Date(dates.fecha_devolucion) <= new Date(dates.fecha_recogida)
  );
  const minDurationError = Boolean(
    !dateOrderError && dates.fecha_recogida && dates.fecha_devolucion &&
    (new Date(dates.fecha_devolucion) - new Date(dates.fecha_recogida)) < rental_min_hours * 3600 * 1000
  );
  useEffect(() => {
    if (!vid || !dates.fecha_recogida || !dates.fecha_devolucion) return;
    if (dateOrderError || minDurationError) { setPricing(null); return; }
    axios.post("/api/pricing/calculate", { vehicle_id: vid, ...dates, ...options, cupon_codigo: cuponAplicado || null })
      .then(r => setPricing(r.data))
      .catch(() => setPricing(null));
  }, [vid, dates, options, dateOrderError, minDurationError, cuponAplicado]);

  const aplicarCupon = () => {
    if (!cuponInput.trim()) return;
    setCuponAplicado(cuponInput.trim().toUpperCase());
  };
  const quitarCupon = () => {
    setCuponAplicado("");
    setCuponInput("");
  };
  const [uploading, setUploading] = useState({});

  const onFile = async (key, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const url = await uploadDoc(file, key);
      setFiles(f => ({ ...f, [key]: url }));
    } catch (err) {
      // fallback to base64 if storage upload fails (still backwards-compatible)
      const b64 = await fileToBase64(file);
      setFiles(f => ({ ...f, [key]: b64 }));
      toast(t("checkout.subidaAlmacenFallo"));
    } finally {
      setUploading(u => ({ ...u, [key]: false }));
    }
  };
  const anyUploading = Object.values(uploading).some(Boolean);

  const estadoVerifInfo = (status) => {
    if (status === "Expired") return { label: t("checkout.verifExpirado"), className: "text-red-600" };
    return { label: t("checkout.verifNoVerificado"), className: "text-red-600" };
  };

  const iniciarVerificacionDoc = async (driver) => {
    const datos = driver === "conductor1" ? customer : conductor2;
    if (!datos.nombre || !datos.apellidos || !datos.dni) {
      toast.error(t("checkout.completaDatosAntesValidar"));
      return;
    }
    if (!validarDniNie(datos.dni)) {
      toast.error(t("checkout.dniInvalido"));
      return;
    }
    setVerificando(driver);
    try {
      localStorage.setItem("pc_checkout_form", JSON.stringify({
        vid, dates, options, customer, tieneConductor2, conductor2, telefonoVerificado,
        verifConductor1, verifConductor2,
      }));
      const { data } = await axios.post("/api/verificacion/iniciar", {
        driver, vehicle_id: vid, nombre: datos.nombre, dni: datos.dni,
        email: driver === "conductor1" ? customer.email : "",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("checkout.verificacionError"));
      setVerificando("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (anyUploading) { toast.error(t("checkout.esperaSubida")); return; }
    if (dateOrderError) { toast.error(t("checkout.errorFechas")); return; }
    if (minDurationError) { toast.error(t("checkout.avisoDuracionMinima", { horas: rental_min_hours })); return; }
    if (!pricing) { toast.error(t("checkout.seleccionaFechas")); return; }
    if (!customer.fecha_nacimiento || !customer.fecha_expedicion_carnet) {
      toast.error(t("checkout.completaNacimientoCarnet"));
      return;
    }
    if (!customer.fecha_validez_carnet) {
      toast.error(t("checkout.completaValidezCarnet"));
      return;
    }
    if (!validarDniNie(customer.dni)) {
      toast.error(t("checkout.dniInvalidoConductor"));
      return;
    }
    if (phone_verification_enabled && !telefonoVerificado) {
      toast.error(t("checkout.verificaTelefonoAntes"));
      return;
    }
    if (id_verification_enabled && !conductor1PreVerificado && verifConductor1?.status !== "Approved") {
      toast.error(t("checkout.validaDocumentosAntes"));
      return;
    }
    if (tieneConductor2) {
      if (!conductor2.nombre || !conductor2.apellidos || !conductor2.dni || !conductor2.direccion || !conductor2.telefono || !conductor2.fecha_nacimiento || !conductor2.fecha_validez_carnet) {
        toast.error(t("checkout.completaConductor2"));
        return;
      }
      if (!validarDniNie(conductor2.dni)) {
        toast.error(t("checkout.dniInvalidoConductor2"));
        return;
      }
      if (id_verification_enabled && verifConductor2?.status !== "Approved") {
        toast.error(t("checkout.validaDocumentosConductor2Antes"));
        return;
      }
      if (!id_verification_enabled && (!files.c2_dni_frontal || !files.c2_dni_trasero || !files.c2_carnet_frontal || !files.c2_carnet_trasero)) {
        toast.error(t("checkout.subeDocsConductor2"));
        return;
      }
    }
    if (pricing.unavailable) { toast.error(t("checkout.fechasNoDisponibles")); return; }
    if (!id_verification_enabled && (!files.dni_frontal || !files.dni_trasero || !files.carnet_frontal || !files.carnet_trasero)) {
      toast.error(t("checkout.subeDocs")); return;
    }
    setLoading(true);
    try {
      const payload = {
        vehicle_id: vid,
        ...dates,
        ...options,
        cupon_codigo: cuponAplicado || null,
        customer: {
          ...customer, ...files,
          telefono_verificado: phone_verification_enabled ? telefonoVerificado : null,
          verificacion_doc_id: id_verification_enabled ? verifConductor1?.verification_id : null,
          conductor2: tieneConductor2 ? {
            ...conductor2,
            dni_frontal: files.c2_dni_frontal,
            dni_trasero: files.c2_dni_trasero,
            carnet_frontal: files.c2_carnet_frontal,
            carnet_trasero: files.c2_carnet_trasero,
            verificacion_doc_id: id_verification_enabled ? verifConductor2?.verification_id : null,
          } : null,
        }, origin_url: window.location.origin,
      };
      const r = await axios.post("/api/checkout/session", payload);
      localStorage.removeItem("pc_checkout_form");
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("checkout.errorPago"));
      setLoading(false);
    }
  };

  if (!vehicle) return <div className="max-w-7xl mx-auto px-4 py-16">{t("checkout.seleccionaVehiculo")}</div>;

  return (
    <section className="section-light py-10" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-6">
          <div className="divider-red mb-3"></div>
          <h1 className="font-display text-3xl sm:text-4xl">{t("checkout.reserva")}: {vehicle.marca_modelo}</h1>
        </div>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[var(--pc-grey)] p-5 rounded-lg">
              <h3 className="font-display text-xl mb-4">{t("checkout.fechas")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.recogida")}</label><DateTimeQuarterInput required value={dates.fecha_recogida} onChange={v => setDates(d => ({ ...d, fecha_recogida: v }))} placeholder={t("home.placeholderRecogida")} dateTestId="checkout-pickup-date" timeTestId="checkout-pickup-time" /></div>
                <div><label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.devolucion")}</label><DateTimeQuarterInput required value={dates.fecha_devolucion} onChange={v => setDates(d => ({ ...d, fecha_devolucion: v }))} min={dates.fecha_recogida || undefined} placeholder={t("home.placeholderDevolucion")} dateTestId="checkout-dropoff-date" timeTestId="checkout-dropoff-time" /></div>
              </div>
              {dateOrderError && (
                <div className="mt-3 text-sm bg-red-100 text-red-700 px-3 py-2 rounded border border-red-300" data-testid="checkout-date-order-error">
                  {t("checkout.avisoFechas")}
                </div>
              )}
              {minDurationError && (
                <div className="mt-3 text-sm bg-red-100 text-red-700 px-3 py-2 rounded border border-red-300" data-testid="checkout-min-duration-error">
                  {t("checkout.avisoDuracionMinima", { horas: rental_min_hours })}
                </div>
              )}
              {pricing?.unavailable && (
                <div className="mt-3 text-sm bg-red-100 text-red-700 px-3 py-2 rounded border border-red-300" data-testid="checkout-unavailable">
                  {t("checkout.conflicto")} {pricing.conflict_range?.fecha_recogida?.slice(0, 16).replace("T", " ")} → {pricing.conflict_range?.fecha_devolucion?.slice(0, 16).replace("T", " ")}
                </div>
              )}
            </div>

            <div className="bg-[var(--pc-grey)] p-5 rounded-lg">
              <h3 className="font-display text-xl mb-4">{t("checkout.segurosExtras")}</h3>
              <div className="space-y-3 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={options.reduccion_franquicia} onChange={e => setOptions(o => ({ ...o, reduccion_franquicia: e.target.checked }))} data-testid="opt-franquicia" /> {t("checkout.reduccionFranquicia")}</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={options.conductor_extra} onChange={e => setOptions(o => ({ ...o, conductor_extra: e.target.checked }))} data-testid="opt-conductor-extra" /> {t("checkout.conductorExtra")}</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={options.conduccion_extranjero} onChange={e => setOptions(o => ({ ...o, conduccion_extranjero: e.target.checked }))} data-testid="opt-extranjero" /> {t("checkout.conduccionExtranjero")}</label>
                <div className="flex items-center gap-2">
                  <span>{t("checkout.packsKm", { precio: precioPack })}</span>
                  <input type="number" min="0" className="pc-input w-24" value={options.packs_km} onChange={e => setOptions(o => ({ ...o, packs_km: parseInt(e.target.value) || 0 }))} data-testid="opt-packs-km" />
                </div>
              </div>
            </div>

            <div className="bg-[var(--pc-grey)] p-5 rounded-lg">
              <h3 className="font-display text-xl mb-4">{t("checkout.datosConductor")}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <input className="pc-input" placeholder={t("checkout.nombre")} required value={customer.nombre} onChange={e => setCustomer({ ...customer, nombre: e.target.value })} data-testid="cust-nombre" />
                <input className="pc-input" placeholder={t("checkout.apellidos")} required value={customer.apellidos} onChange={e => setCustomer({ ...customer, apellidos: e.target.value })} data-testid="cust-apellidos" />
                <input className="pc-input" placeholder={t("checkout.dniPasaporte")} required value={customer.dni} onChange={e => { setCustomer({ ...customer, dni: e.target.value }); if (dniError) setDniError(""); }} onBlur={e => setDniError(validarDniNie(e.target.value) ? "" : t("checkout.dniInvalido"))} data-testid="cust-dni" />
                {dniError && <div className="text-red-600 text-xs mt-1" data-testid="cust-dni-error">{dniError}</div>}
                <input className="pc-input" placeholder={t("checkout.telefono")} required value={customer.telefono} onChange={e => { setCustomer({ ...customer, telefono: e.target.value }); setTelefonoVerificado(false); }} data-testid="cust-telefono" />
                {phone_verification_enabled && (
                  <div className="col-span-2 -mt-2">
                    <PhoneVerification phoneNumber={customer.telefono} verified={telefonoVerificado} onVerified={setTelefonoVerificado} />
                  </div>
                )}
                <input className="pc-input col-span-2" placeholder={t("checkout.direccionCompleta")} required value={customer.direccion} onChange={e => setCustomer({ ...customer, direccion: e.target.value })} data-testid="cust-direccion" />
                <input className="pc-input col-span-2" type="email" placeholder={t("checkout.email")} required value={customer.email} onChange={e => setCustomer({ ...customer, email: e.target.value })} data-testid="cust-email" />
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.nacimiento")}</label>
                  <DateSelect value={customer.fecha_nacimiento} onChange={v => setCustomer({ ...customer, fecha_nacimiento: v })} minYear={new Date().getFullYear() - 100} maxYear={new Date().getFullYear() - 16} testId="cust-nac" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.expedicionCarnet")}</label>
                  <DateSelect value={customer.fecha_expedicion_carnet} onChange={v => setCustomer({ ...customer, fecha_expedicion_carnet: v })} minYear={new Date().getFullYear() - 80} maxYear={new Date().getFullYear()} testId="cust-carnet" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.validezCarnet")}</label>
                  <DateSelect value={customer.fecha_validez_carnet} onChange={v => setCustomer({ ...customer, fecha_validez_carnet: v })} minYear={new Date().getFullYear()} maxYear={new Date().getFullYear() + 20} testId="cust-carnet-validez" />
                </div>
              </div>
              {id_verification_enabled && (
                <div className="mt-3">
                  {conductor1PreVerificado ? (
                    <div className="text-xs text-green-600 flex items-center gap-1" data-testid="verif-conductor1-cuenta-ok">✓ {t("checkout.documentoValidadoCuenta")}</div>
                  ) : verifConductor1?.status === "Approved" ? (
                    <div className="text-xs text-green-600 flex items-center gap-1" data-testid="verif-conductor1-ok">✓ {t("checkout.documentoValidado")}</div>
                  ) : (
                    <button type="button" onClick={() => iniciarVerificacionDoc("conductor1")} disabled={verificando === "conductor1"} className="text-xs px-3 py-2 bg-neutral-800 text-white rounded hover:bg-neutral-700 disabled:opacity-50" data-testid="verif-conductor1-btn">
                      {verificando === "conductor1" ? t("checkout.verificando") : t("checkout.validacionOnlineDocumentos")}
                    </button>
                  )}
                  {!conductor1PreVerificado && verifConductor1 && verifConductor1.status !== "Approved" && (
                    <div className={`text-xs mt-1 ${estadoVerifInfo(verifConductor1.status).className}`} data-testid="verif-conductor1-estado">{estadoVerifInfo(verifConductor1.status).label}</div>
                  )}
                </div>
              )}
              <label className="flex items-center gap-2 mt-4 text-sm">
                <input type="checkbox" checked={tieneConductor2} onChange={e => setTieneConductor2(e.target.checked)} data-testid="opt-conductor2" />
                {t("checkout.anadirConductor2")}
              </label>

              {tieneConductor2 && (
                <div className="grid grid-cols-2 gap-3 text-sm mt-3 border-t border-neutral-300 pt-3">
                  <input className="pc-input" placeholder={t("checkout.nombre")} required value={conductor2.nombre} onChange={e => setConductor2({ ...conductor2, nombre: e.target.value })} data-testid="cust2-nombre" />
                  <input className="pc-input" placeholder={t("checkout.apellidos")} required value={conductor2.apellidos} onChange={e => setConductor2({ ...conductor2, apellidos: e.target.value })} data-testid="cust2-apellidos" />
                  <input className="pc-input" placeholder={t("checkout.dniPasaporte")} required value={conductor2.dni} onChange={e => { setConductor2({ ...conductor2, dni: e.target.value }); if (dniError2) setDniError2(""); }} onBlur={e => setDniError2(validarDniNie(e.target.value) ? "" : t("checkout.dniInvalido"))} data-testid="cust2-dni" />
                  {dniError2 && <div className="text-red-600 text-xs mt-1">{dniError2}</div>}
                  <input className="pc-input" placeholder={t("checkout.telefono")} required value={conductor2.telefono} onChange={e => setConductor2({ ...conductor2, telefono: e.target.value })} data-testid="cust2-telefono" />
                  <input className="pc-input col-span-2" placeholder={t("checkout.direccionCompleta")} required value={conductor2.direccion} onChange={e => setConductor2({ ...conductor2, direccion: e.target.value })} data-testid="cust2-direccion" />
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.nacimiento")}</label>
                    <DateSelect value={conductor2.fecha_nacimiento} onChange={v => setConductor2({ ...conductor2, fecha_nacimiento: v })} minYear={new Date().getFullYear() - 100} maxYear={new Date().getFullYear() - 16} testId="cust2-nac" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.expedicionCarnet")}</label>
                    <DateSelect value={conductor2.fecha_expedicion_carnet} onChange={v => setConductor2({ ...conductor2, fecha_expedicion_carnet: v })} minYear={new Date().getFullYear() - 80} maxYear={new Date().getFullYear()} testId="cust2-carnet" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs uppercase tracking-widest text-neutral-500">{t("checkout.validezCarnet")}</label>
                    <DateSelect value={conductor2.fecha_validez_carnet} onChange={v => setConductor2({ ...conductor2, fecha_validez_carnet: v })} minYear={new Date().getFullYear()} maxYear={new Date().getFullYear() + 20} testId="cust2-carnet-validez" />
                  </div>
                  <textarea className="pc-input col-span-2" placeholder={t("checkout.observacionesOpcional")} value={conductor2.observaciones} onChange={e => setConductor2({ ...conductor2, observaciones: e.target.value })} data-testid="cust2-observaciones" />
                  {id_verification_enabled ? (
                    <div className="col-span-2">
                      {verifConductor2?.status === "Approved" ? (
                        <div className="text-xs text-green-600 flex items-center gap-1" data-testid="verif-conductor2-ok">✓ {t("checkout.documentoValidado")}</div>
                      ) : (
                        <button type="button" onClick={() => iniciarVerificacionDoc("conductor2")} disabled={verificando === "conductor2"} className="text-xs px-3 py-2 bg-neutral-800 text-white rounded hover:bg-neutral-700 disabled:opacity-50" data-testid="verif-conductor2-btn">
                          {verificando === "conductor2" ? t("checkout.verificando") : t("checkout.validacionOnlineDocumentos")}
                        </button>
                      )}
                      {verifConductor2 && verifConductor2.status !== "Approved" && (
                        <div className={`text-xs mt-1 ${estadoVerifInfo(verifConductor2.status).className}`} data-testid="verif-conductor2-estado">{estadoVerifInfo(verifConductor2.status).label}</div>
                      )}
                    </div>
                  ) : (
                    <div className="col-span-2">
                      <h4 className="font-display text-lg mt-2 mb-3">{t("checkout.docsConductor2")}</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {[
                          ["c2_dni_frontal", t("checkout.dniFrontal")],
                          ["c2_dni_trasero", t("checkout.dniTrasero")],
                          ["c2_carnet_frontal", t("checkout.carnetFrontal")],
                          ["c2_carnet_trasero", t("checkout.carnetTrasero")],
                        ].map(([k, label]) => (
                          <div key={k}>
                            <label className="text-xs uppercase tracking-widest text-neutral-500">{label}</label>
                            <input type="file" accept="image/*" required={!files[k]} disabled={uploading[k]} onChange={e => onFile(k, e.target.files[0])} className="block w-full text-xs mt-1 disabled:opacity-50" data-testid={`file-${k}`} />
                            {uploading[k] && (
                              <div className="flex items-center gap-1 text-xs text-neutral-500 mt-1" data-testid={`uploading-${k}`}>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("checkout.subiendo")}
                              </div>
                            )}
                            {!uploading[k] && files[k] && <div className="text-xs text-green-600 mt-1">{t("checkout.cargado")}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!id_verification_enabled && (
                <>
                  <h4 className="font-display text-lg mt-5 mb-3">{t("checkout.docsObligatorio")}</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      ["dni_frontal", t("checkout.dniFrontal")],
                      ["dni_trasero", t("checkout.dniTrasero")],
                      ["carnet_frontal", t("checkout.carnetFrontal")],
                      ["carnet_trasero", t("checkout.carnetTrasero")],
                    ].map(([k, label]) => (
                      <div key={k}>
                        <label className="text-xs uppercase tracking-widest text-neutral-500">{label}</label>
                        <input type="file" accept="image/*" required={!files[k]} disabled={uploading[k]} onChange={e => onFile(k, e.target.files[0])} className="block w-full text-xs mt-1 disabled:opacity-50" data-testid={`file-${k}`} />
                        {uploading[k] && (
                          <div className="flex items-center gap-1 text-xs text-neutral-500 mt-1" data-testid={`uploading-${k}`}>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("checkout.subiendo")}
                          </div>
                        )}
                        {!uploading[k] && files[k] && <div className="text-xs text-green-600 mt-1">{t("checkout.cargado")}</div>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-black text-white p-5 rounded-lg sticky top-20" data-testid="checkout-summary">
              <h3 className="font-display text-xl mb-4">{t("checkout.tuReserva")}</h3>
              <img src={vehicle.imagen_url} alt={vehicle.marca_modelo} className="w-full aspect-[4/3] object-cover rounded mb-3" />
              <div className="text-sm space-y-1 mb-4">
                <div className="font-semibold">{vehicle.marca_modelo}</div>
                <div className="text-neutral-400 text-xs uppercase tracking-wider">{vehicle.categoria}</div>
              </div>
              {pricing ? (
                <div className="text-sm space-y-1 border-t border-neutral-800 pt-3">
                  <Row k={t("checkout.dias")} v={pricing.dias} />
                  <Row k={`${t("checkout.coche")} (${pricing.precio_dia_efectivo}€/día${pricing.descuento_pct ? ` · -${pricing.descuento_pct}%` : ""})`} v={`${pricing.subtotal_coche.toFixed(2)} €`} />
                  {pricing.seguro_total > 0 && <Row k={`${t("checkout.seguro")} (${t("checkout.franquicia")} ${pricing.franquicia}€)`} v={`${pricing.seguro_total.toFixed(2)} €`} />}
                  {pricing.extras_total > 0 && <Row k={t("checkout.extras")} v={`${pricing.extras_total.toFixed(2)} €`} />}
                  {pricing.km_extra_total > 0 && <Row k={`${t("checkout.packsKm", { precio: precioPack }).replace(":", "")} (${pricing.packs_km})`} v={`${pricing.km_extra_total.toFixed(2)} €`} />}
                  <Row k={t("checkout.kmIncluidos")} v={`${pricing.km_incluidos}`} />
                  {pricing.cupon_descuento > 0 && (
                    <Row k={`${t("checkout.cuponDescuento")} (${pricing.cupon_codigo})`} v={`-${pricing.cupon_descuento.toFixed(2)} €`} />
                  )}
                  <div className="border-t border-neutral-800 pt-2 mt-2 flex justify-between font-display text-2xl text-[var(--pc-red)]">
                    <span>{t("checkout.total")}</span><span>{pricing.total.toFixed(2)} €</span>
                  </div>
                </div>
              ) : <div className="text-sm text-neutral-400">{t("checkout.eligeFechasDesglose")}</div>}

              {pricing && (
                <div className="border-t border-neutral-800 pt-3 mt-3" data-testid="checkout-cupon-box">
                  {cuponAplicado ? (
                    <div className="flex items-center justify-between text-sm">
                      <span className={pricing.cupon_codigo ? "text-green-400" : "text-red-400"}>
                        {pricing.cupon_codigo ? `✓ ${t("checkout.cuponAplicado")}: ${pricing.cupon_codigo}` : pricing.cupon_error || t("checkout.cuponInvalido")}
                      </span>
                      <button type="button" onClick={quitarCupon} className="text-xs underline text-neutral-400" data-testid="checkout-cupon-quitar">
                        {t("checkout.cuponQuitar")}
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input className="pc-input text-black flex-1" placeholder={t("checkout.cuponPlaceholder")} value={cuponInput} onChange={e => setCuponInput(e.target.value.toUpperCase())} data-testid="checkout-cupon-input" />
                      <button type="button" onClick={aplicarCupon} className="text-xs px-3 py-2 bg-neutral-800 rounded hover:bg-neutral-700" data-testid="checkout-cupon-aplicar">
                        {t("checkout.cuponAplicar")}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" disabled={loading || pricing?.unavailable || dateOrderError || minDurationError || anyUploading || (id_verification_enabled && ((!conductor1PreVerificado && verifConductor1?.status !== "Approved") || (tieneConductor2 && verifConductor2?.status !== "Approved")))} className="btn-red w-full py-3 mt-4 disabled:opacity-50" data-testid="checkout-pay-btn">
                {anyUploading ? t("checkout.subiendoDocumentos") : loading ? t("checkout.procesando") : pricing?.unavailable ? t("checkout.fechasNoDisponiblesBtn") : t("checkout.pagarTarjeta")}
              </button>
              <div className="text-xs text-neutral-500 mt-2 text-center">{t("checkout.paypalProximamente")}</div>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-neutral-300">{k}</span><span>{v}</span></div>;
}
